/**
 * Monad, comonad, Kleisli-composition, and graded-monad ladder
 * diagrams.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useId } from "react";
import { GRAY } from "../../../design-system/tokens.js";
import {
  DIAGRAM_MONO,
  STROKE,
  LABEL,
  ArrowDefs,
  ObjectNode,
  Arrow,
  FunctorBox,
} from "./primitives.js";

/**
 * MonadDiagram: a compact picture of (T, η, μ).
 *
 *   A ── η ── ▶ T(A) ◀── μ ── T(T(A))
 *
 * The unit `η` inserts a value into the effect; the multiplication `μ`
 * collapses two layers of `T` into one. Both points to the same `T(A)`
 * so the diagram visually reads as "all roads lead to T(A)".
 */
export function MonadDiagram({
  accent = STROKE,
  effect = "T",
  carrier = "A",
  ariaLabel = "Monad (T, η, μ): unit T : A ⇒ T(A) and multiplication μ : T(T(A)) ⇒ T(A)",
}: {
  readonly accent?: string;
  readonly effect?: string;
  readonly carrier?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  return (
    <svg
      viewBox="0 0 360 130"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <ObjectNode cx={44} cy={70} label={carrier} accent={accent} />
      <FunctorBox cx={180} cy={70} label={carrier} functor={effect} accent={accent} />
      <FunctorBox cx={312} cy={70} label={`${effect}${carrier}`} functor={effect} accent={accent} />
      <Arrow x1={58} y1={70} x2={156} y2={70} label="η" markerId={markerId} color={accent} />
      <Arrow x1={288} y1={70} x2={204} y2={70} label="μ" markerId={markerId} color={accent} />
      <text
        x={180}
        y={118}
        textAnchor="middle"
        fontSize="11"
        fontFamily={DIAGRAM_MONO}
        fill={GRAY[600]}
      >
        η : Id ⇒ {effect} &nbsp;·&nbsp; μ : {effect}∘{effect} ⇒ {effect}
      </text>
    </svg>
  );
}

/**
 * ComonadDiagram: a compact picture of (W, ε, δ).
 *
 *   A ◀── ε ── W(A) ── δ ──▶ W(W(A))
 *
 * The counit `ε` extracts a value from the context; the comultiplication
 * `δ` reveals one more layer of `W` from the context.
 */
export function ComonadDiagram({
  accent = STROKE,
  effect = "W",
  carrier = "A",
  ariaLabel = "Comonad (W, ε, δ): counit ε : W(A) ⇒ A and comultiplication δ : W(A) ⇒ W(W(A))",
}: {
  readonly accent?: string;
  readonly effect?: string;
  readonly carrier?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  return (
    <svg
      viewBox="0 0 360 130"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <ObjectNode cx={44} cy={70} label={carrier} accent={accent} />
      <FunctorBox cx={180} cy={70} label={carrier} functor={effect} accent={accent} />
      <FunctorBox cx={312} cy={70} label={`${effect}${carrier}`} functor={effect} accent={accent} />
      <Arrow x1={156} y1={70} x2={58} y2={70} label="ε" markerId={markerId} color={accent} />
      <Arrow x1={204} y1={70} x2={288} y2={70} label="δ" markerId={markerId} color={accent} />
      <text
        x={180}
        y={118}
        textAnchor="middle"
        fontSize="11"
        fontFamily={DIAGRAM_MONO}
        fill={GRAY[600]}
      >
        ε : {effect} ⇒ Id &nbsp;·&nbsp; δ : {effect} ⇒ {effect}∘{effect}
      </text>
    </svg>
  );
}

/**
 * KleisliCompositionDiagram: visualizes Kleisli composition of monadic arrows.
 *
 *   A ── f ─▶ T(B) ── T(g) ─▶ T(T(C)) ── μ ─▶ T(C)
 *
 * The combined arrow `g ∘ₜ f = μ ∘ T(g) ∘ f` is shown as a curved arc above
 * the three sequential steps so the reader sees that "bind" is exactly
 * "lift, then collapse".
 */
export function KleisliCompositionDiagram({
  accent = STROKE,
  effect = "T",
  ariaLabel = "Kleisli composition: A → T(B), B → T(C) composes via T(g) ∘ f then μ",
}: {
  readonly accent?: string;
  readonly effect?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  const subtleMarkerId = `${markerId}-subtle`;
  return (
    <svg
      viewBox="0 0 460 160"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <ArrowDefs id={subtleMarkerId} color={GRAY[600]} />
      <ObjectNode cx={32} cy={92} label="A" accent={accent} />
      <FunctorBox cx={140} cy={92} label="B" functor={effect} accent={accent} />
      <FunctorBox cx={272} cy={92} label={`${effect}C`} functor={effect} accent={accent} />
      <FunctorBox cx={420} cy={92} label="C" functor={effect} accent={accent} />
      <Arrow x1={46} y1={92} x2={116} y2={92} label="f" markerId={markerId} color={accent} />
      <Arrow
        x1={168}
        y1={92}
        x2={248}
        y2={92}
        label={`${effect}(g)`}
        markerId={markerId}
        color={accent}
      />
      <Arrow x1={300} y1={92} x2={396} y2={92} label="μ" markerId={markerId} color={accent} />
      <Arrow
        x1={36}
        y1={82}
        x2={416}
        y2={82}
        label="g ∘ₜ f"
        markerId={subtleMarkerId}
        color={GRAY[600]}
        variant="dashed"
        curve={42}
      />
    </svg>
  );
}

/**
 * GradeLadderDiagram: a vertical ladder of grades climbed by Kleisli
 * composition, illustrating that grades accumulate by `·` and never decrease.
 *
 * Each rung is labeled with a grade `kᵢ`; arrows on the right of each rung
 * carry the corresponding Kleisli arrow with its grade subscript. The unit
 * `e` lives at the bottom rung.
 */
export interface GradeLadderRung {
  readonly grade: string;
  readonly note?: string;
  readonly accent?: string;
}

export function GradeLadderDiagram({
  rungs,
  accent = STROKE,
  ariaLabel = "Grade ladder for a graded monad: Kleisli composition climbs the ladder by combining grades",
}: {
  readonly rungs: readonly GradeLadderRung[];
  readonly accent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  const rowHeight = 50;
  const ladderHeight = rungs.length * rowHeight + 30;
  const x = 90;
  return (
    <svg
      viewBox={`0 0 360 ${ladderHeight}`}
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <text
        x={x - 30}
        y={20}
        textAnchor="end"
        fontSize="11"
        fontWeight={600}
        fill={GRAY[500]}
        fontFamily={DIAGRAM_MONO}
      >
        higher
      </text>
      <text
        x={x - 30}
        y={ladderHeight - 4}
        textAnchor="end"
        fontSize="11"
        fontWeight={600}
        fill={GRAY[500]}
        fontFamily={DIAGRAM_MONO}
      >
        lower
      </text>
      {rungs.map((rung, i) => {
        const y = ladderHeight - 24 - i * rowHeight;
        const ringColor = rung.accent ?? accent;
        return (
          <g key={`${rung.grade}-${String(i)}`}>
            <line
              x1={x - 6}
              y1={y}
              x2={x + 6}
              y2={y}
              stroke={ringColor}
              strokeWidth={3}
              strokeLinecap="round"
            />
            <circle cx={x} cy={y} r={5} fill={ringColor} />
            <text
              x={x + 18}
              y={y + 4}
              fontSize="13"
              fontFamily={DIAGRAM_MONO}
              fontWeight={600}
              fill={LABEL}
            >
              T
              <tspan baselineShift="sub" fontSize="9">
                {rung.grade}
              </tspan>
              {rung.note !== undefined && (
                <tspan dx="10" fill={GRAY[600]} fontWeight={400} fontSize="11">
                  {rung.note}
                </tspan>
              )}
            </text>
          </g>
        );
      })}
      {/* Vertical climbing arrow on the left */}
      <Arrow
        x1={x - 36}
        y1={ladderHeight - 24}
        x2={x - 36}
        y2={32}
        label="·"
        subLabel="grade·grade"
        markerId={markerId}
        color={accent}
      />
    </svg>
  );
}
